<script setup>

import { ref, computed, onMounted, nextTick } from 'vue'
import { DxForm } from 'devextreme-vue/form'
import { DxTagBox } from 'devextreme-vue/tag-box'
import DxValidator, { DxCustomRule } from 'devextreme-vue/validator'
import validationEngine from 'devextreme/ui/validation_engine'
import { DxTextBox } from 'devextreme-vue'
import { DxLoadPanel } from 'devextreme-vue/load-panel'
import { useFormData } from '@/composables/garage/event-scheduler'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { menuRoutesEnum } from '@/router'
import { getI18n } from '@/plugins/i18n'
import { useDialog } from '@/composables/dialog'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useInfoBikeSearch } from '@/composables/registry/info-bike-search'

import { getDirtyFields } from '@/lib/utils'

import { useDocumentService } from '@/services/document.service'
import { useSubjectService } from '@/services/subject.service'

import { usePortalStore } from '@/stores/portal.store'
import { useGarageStore } from '@/stores/garage.store'
import { usePartialSave } from '@/composables/partial-save'

import { registryApi } from '@/apis/registry.api'

import CarServiceNumberBox from '@/components/shared/CarServiceNumberBox.vue'
import ChipStatus from '@/components/shared/ChipStatus.vue'
import CustomerDropDownBox from '@/components/shared/CustomerDropDownBox.vue'
import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
// import GarageEventSchedulerProductGrid from '@/components/garage/GarageEventSchedulerProductGrid.vue'  --- US 623 https://dev.azure.com/softway-it/ArcoWeb/_workitems/edit/623
import InformationToolTip from '@/components/shared/InformationToolTip.vue'
import InfoBikeSearchCaptionButton from '@/components/shared/InfoBikeSearchCaptionButton.vue'
import InfoBikePlateNumberEditor from '@/components/shared/InfoBikePlateNumberEditor.vue'
import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import ServiceHistoryGrid from '@/components/garage/ServiceHistoryGrid.vue'
import VehicleDropDownBox from '@/components/shared/VehicleDropDownBox.vue'
import InfoTooltipIcon from '@/components/shared/InfoTooltipIcon.vue'

const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const garageStore = useGarageStore()

const formRef = ref(null)
const i18n = getI18n()
const { genericDialog } = useDialog()

const selected = ref([])
const appointmentNotifySelected = ref([])
const isOwnerSaveDisabled = ref(true)
const isVehicleSaveDisabled = ref(true)
const isChangeOwnerDisabled = ref(true)
const currentCarService = ref(null)

const currentVehicleFuelType = ref(null)
const currentCustomerSubjectType = ref(null)

const { partialSave } = usePartialSave()
const { extractReferenceData } = useDocumentService()
const { isNameExtMandatory } = useSubjectService()

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  options: {
    type: Object,
    default: null
  },
})
const formData = ref(structuredClone(props.data))
const formDataInitial = structuredClone(props.data)
const isReadOnly = computed(() => props.data?.worksheets?.length > 0 || props.options?.readOnly)
const validationGroup = 'event-scheduler-form'
const loading = ref(false)

const {
  infoBikeSearchDisabled, showPulse, pulseCount,
  currentInfoBikeBrandCode, currentInfoBikeModelCode,
  onPlateBlur, infoBikeSearch, loadCreditsAsync, infoBikeCascadeHandlers, infoBikeFormGroup
} = useInfoBikeSearch({
  getEditor: (field) => formRef.value?.instance?.getEditor(field),
  getEditingData: () => formRef.value?.formData,
  setVehicle: (vehicle, options) => {
    formData.value.vehicle_id = vehicle?.id ?? null
    populateVehicleById(vehicle?.id ?? null, false, vehicle, options?.preserveOwner)
  },
  beginLoading: () => {
    loading.value = true
    formRef.value?.instance?.option('disabled', true)
  },
  endLoading: () => {
    loading.value = false
    formRef.value?.instance?.option('disabled', false)
  },
  registryStore, garageStore, portalStore
})

const infoBikeCascade = infoBikeCascadeHandlers(formRef)

const satisfiesRequiredRules = (obj, rules = []) => {
  return rules.every(rule => {
    if (typeof rule === 'string') {
      return !!obj?.[rule]
    }

    if (Array.isArray(rule)) {
      return rule.some(field => !!obj?.[field])
    }

    return false
  })
}

const isSaveDisabled = (key, has_key, requiredFields) => {
  if (has_key) return true

  const formData = formRef.value?.formData
  const obj = formData?.[key]
  if (!obj) return true

  if (key === 'vehicle' && !formData.customer_id) return true

  if (!satisfiesRequiredRules(obj, requiredFields)) return true

  if (key === 'customer' && isNameExtMandatory(obj.subject_type_ref) && !obj.name_ext) return true

  if (key === 'vehicle') {
    const interventionType = garageStore.interventionTypes.find(i => i.id === formData?.intervention_type_ref)
    if (interventionType?.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE && !formData.km) return true
  }

  return false
}

const mechanicOptions = computed(() =>
  (webSiteStore.userDetails || [])
  .filter(i => i.is_mechanic && (i.deleted_at === null || (formData.value.mechanics || []).includes(i.id)))
  .map(m => ({
    label: `${m.name} ${m.name_ext}`,
    value: m.id,
  }))
)

const customerFields = ['customer.subject_type_ref', 'customer.subject_sub_type_ref', 'customer.name_ext', 'customer.name', 'customer.phone']
const customerInfoFields = ['customer.email', 'customer.mobile']
const disabledOwnerFields = ['subject_type_ref', 'subject_sub_type_ref', 'mobile', 'name']
const vehicleFields = ['vehicle.plate_number', 'vehicle.supplier_brand_ref', 'vehicle.name', 'vehicle.color', 'vehicle.engine_size']
const disabledVehicleFields = [['plate_number', 'vin'], 'name', 'supplier_brand_ref']

const makeFieldsReadOnly = (fields, readOnlyValue) => {
  fields.forEach((field) => {
    const editor = formRef.value.instance.getEditor(field)
    if (editor) {
      editor.option('readOnly', isReadOnly.value || readOnlyValue)
    }
  })
  customerInfoFields.forEach((field) => {
    const editor = formRef.value.instance.getEditor(field)
    if (editor) {
      editor.option('readOnly', isReadOnly.value)
    }
  })
}

const formItems = useFormData(
  selected, currentVehicleFuelType, currentCustomerSubjectType, currentCarService,
  formRef, isOwnerSaveDisabled, isVehicleSaveDisabled, isReadOnly, isSaveDisabled,
  { infoBikeCascade, infoBikeSearchDisabled, showPulse, pulseCount, currentInfoBikeBrandCode, currentInfoBikeModelCode, infoBikeSearch, onPlateBlur, infoBikeFormGroup }
)

const populateCustomer = async(customer_id, customer = null) => {
  formData.value.customer = customer

  if (customer_id && !formData.value.customer) {
    formData.value.customer = {}
    const currentCustomer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === customer_id)
    formData.value.customer.subject_type_ref = currentCustomer?.subject_type_ref
    formData.value.customer.subject_sub_type_ref = currentCustomer?.subject_sub_type_ref
    formData.value.customer.name = currentCustomer?.name
    formData.value.customer.name_ext = currentCustomer?.name_ext
    formData.value.customer.phone = currentCustomer?.phone
    formData.value.customer.mobile = currentCustomer?.mobile
    formData.value.customer.email = currentCustomer?.email

    isChangeOwnerDisabled.value = (customer_id === formData.value?.vehicle?.customer_id || !formData.value?.vehicle_id)
  }

  nextTick(() => {
    makeFieldsReadOnly(customerFields, !!formData.value.customer_id || props.options?.readOnly)
    makeFieldsReadOnly(vehicleFields, !!formData.value.vehicle_id || props.options?.readOnly)
  })
}
const populateVehicleById = async(vehicle_id, firstLoad = false, selectedVehicle = null, preserveOwner = false) => {
  formData.value.vehicle = {}
  formData.value.km = null

  if (vehicle_id) {
    const currentVehicle = selectedVehicle ?? (await registryStore.getOrFetchVehiclesAsync()).find(c => c.id === vehicle_id)
    formData.value.vehicle.id = vehicle_id
    formData.value.vehicle.name = currentVehicle?.name
    formData.value.vehicle.color = currentVehicle?.color
    formData.value.vehicle.engine_size = currentVehicle?.engine_size
    formData.value.vehicle.plate_number = currentVehicle?.plate_number
    formData.value.km = currentVehicle?.km
    formData.value.vehicle.customer_id = currentVehicle?.customer_id
    formData.value.vehicle.vin = currentVehicle?.vin
    formData.value.vehicle.supplier_brand_ref = currentVehicle?.supplier_brand_ref

    currentVehicleFuelType.value = currentVehicle?.fuel_type

    if (!firstLoad && !preserveOwner) {
      formData.value.customer_id = currentVehicle?.customer_id || null
      populateCustomer(currentVehicle?.customer_id)
    }
  }
}

const addNewMechanic = () => {
  window.open(menuRoutesEnum.MECHANICS, '_blank')
}

const refreshMechanics = async() => await webSiteStore.getOrFetchUserDetailsAsync()

const onCustomerChanged = async(e, cellInfo, customer_id = null) => {
  const currentCustomerId = e?.value?.id || customer_id || null
  formData.value.customer_id = currentCustomerId
  await populateCustomer(currentCustomerId)

  isVehicleSaveDisabled.value = isSaveDisabled('vehicle', !!formData.value?.vehicle_id, disabledVehicleFields)
}

const onVehicleChanged = async(e, cellInfo) => {
  formData.value.vehicle_id = e.value?.id || null
  await populateVehicleById(e.value?.id, false, e.value)

  if (formData.value?.vehicle_id &&
  formData.value?.intervention_type_ref === garageStore.getLocalizedInterventionTypes().find(el => el.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE)?.id) {
    const res = await registryStore.api.getVehicleNextCarServiceAsync(formData.value.vehicle_id)
    formData.value.car_service = currentCarService.value = res.data
  } else {
    formData.value.car_service = null
  }
}

const onCourtesyVehicleChanged = async(e, cellInfo) => {
  formData.value.courtesy_vehicle = e.value?.id || null
}

const onDepositChanged = async(e, cellInfo) => {
  formData.value.deposit = e.value?.id || null
}

const onVinChanged = (e) => {
  formData.value.vehicle.vin = e.value?.toUpperCase() || ''
  isVehicleSaveDisabled.value = isSaveDisabled('vehicle', !!formRef.value?.formData?.vehicle_id, disabledVehicleFields)
  formRef.value?.instance?.validate()
}

const getArrayFromFlagValue = (value) => {
  return Object.values(portalStore.appointmentNotifyEnum)
    .filter(flag => (value & flag) === flag)
}

const saveNewOwner = async() => {
  const owner = formData.value?.customer
  if (!owner || !owner.subject_sub_type_ref || !owner.subject_type_ref || !owner.name || !owner.mobile) {
    formRef.value?.instance?.validate()
    return null
  }

  const nextIdResponse = await registryStore.api.getSubjectNextIdAsync()
  owner.code = nextIdResponse.data
  owner.type = registryStore.subjectEntityTypes.CUSTOMER
  owner.customer ??= {}
  owner.customer.discount_list_ref = registryStore.getLocalizedDiscountLists()[0]?.id
  delete owner.id
  delete owner.address

  const res = await partialSave(owner, registryApi.createSubjectAsync, { grid: formRef.value.instance, target: menuRoutesEnum.SUBJECTS, queryFields: ['code'], entityName: 'common.customer'})
  if (res?.customer) {
    await registryStore.fetchCustomersAsync(true)
    formData.value.customer_id = res?.customer?.id
    formRef.value?.instance?.updateData('customer_id', res.customer.id)
    formRef.value?.instance?.repaint()
    makeFieldsReadOnly(customerFields, !!formData.value.customer_id)
    await onCustomerChanged(null, null, res?.customer?.id)
    return res.customer.id
  }

  return null
}

const saveNewVehicle = async() => {
  const vehicle = formData.value?.vehicle

  if (!vehicle || !formData.value?.customer_id || (!vehicle.plate_number && !vehicle.vin) || !vehicle.name || !vehicle.supplier_brand_ref) {
    formRef.value?.instance?.validate()
    return null
  }

  const nextIdResponse = await registryStore.api.getVehicleNextIdAsync()
  vehicle.id = nextIdResponse.data
  vehicle.status = registryStore.vehicleStatusTypesEnum.TRANSITED
  vehicle.customer_id = formData.value?.customer_id

  const res = await partialSave(vehicle, registryApi.createVehicleAsync, { grid: formRef.value.instance, target: menuRoutesEnum.VEHICLES, queryFields: ['vin'], entityName: 'common.vehicle'})
  if (res) {
    await registryStore.fetchVehiclesAsync(true)
    formData.value.vehicle_id = res?.id
    formRef.value?.instance?.updateData('vehicle_id', res.id)
    formData.value.vehicle.vin = res?.vin
    formRef.value?.instance?.repaint()
    makeFieldsReadOnly(vehicleFields, !!formData.value.vehicle_id)
    makeFieldsReadOnly(customerFields, !!formData.value.customer_id)
    isVehicleSaveDisabled.value = true
    return res.id
  }

  return null
}

const addNewVehicleOwner = async() => {
  const newMovement = webSiteStore.newVehicleOwnerMovement(formData.value.customer_id, formData.value.vehicle_id)

  genericDialog(
    i18n.t('common.update'),
    i18n.t('common.updateOwner'),
    { cancel: true, persistent: true }
  ).onOk(async() => {
    await registryApi.createVehicleMovementAsync(newMovement)
    await registryStore.fetchVehiclesAsync(true)

    isChangeOwnerDisabled.value = true
  })
}

const mechanicValidation = (e) => {
  return e.value !== null && e.value.length > 0
}

const onSave = async(e) => {
  const form = formRef.value?.instance
  if (!form) return

  let validationResult = validationEngine.validateGroup(validationGroup)
  const missingEntityReferences = ['customer_id', 'vehicle_id'].filter(field => !formData.value[field])
  const brokenRulesCount = validationResult.brokenRules?.length ?? 0

  if (missingEntityReferences.length > 0 && (validationResult.isValid || (brokenRulesCount > 0 && brokenRulesCount <= missingEntityReferences.length))) {
    form.option('disabled', true)
    try {
    // Customer
      if (!formData.value.customer_id) {
        const isValidCustomer = !isSaveDisabled('customer', false, disabledOwnerFields)
        if (!isValidCustomer || !(await saveNewOwner())) return null
      }

      // Vehicle
      if (!formData.value.vehicle_id) {
        const isValidVehicle = !isSaveDisabled('vehicle', false, disabledVehicleFields)
        if (!isValidVehicle || !(await saveNewVehicle())) return null
      }
    } finally {
      form.option('disabled', false)
    }

    await nextTick()
    validationResult = validationEngine.validateGroup(validationGroup)
  }

  if (validationResult.isValid && formData.value.customer_id && formData.value.vehicle_id) {
    formData.value.appointment_notify = appointmentNotifySelected.value.reduce((acc, val) => acc + val, 0)

    const isUpdate = !!formData.value.id
    if (isUpdate) {
      const dirty = getDirtyFields(formData.value, formDataInitial)
      return { id: formData.value.id, ...dirty }
    }

    return { ...formData.value }
  }

  return null
}
defineExpose({ onSave })

const onFormContentReady = async() => {
  const value = formData.value.garage_reason_ref
  if (!value) return

  const currentGarageReason = garageStore.garageReasons.find(i => i.id === value)
  const interventionType = garageStore.interventionTypes.find(
    i => i.id === currentGarageReason?.intervention_type_ref
  )

  if (interventionType?.has_car_service && formData.value?.vehicle_id) {
    const res = await registryStore.api.getVehicleNextCarServiceAsync(formData.value.vehicle_id)
    currentCarService.value = res.data
  }
}

const onVinInitialized = (e) => {
  const form = formRef.value?.instance
  if (!form) return

  form._editors ||= {}
  form._editors['vehicle.vin'] = e.component
}

onMounted(() => {
  loadCreditsAsync()
  webSiteStore.getOrFetchUserDetailsAsync()
  appointmentNotifySelected.value = getArrayFromFlagValue(formData.value?.appointment_notify)
  formRef.value?.instance?.repaint()

  populateVehicleById(formData.value?.vehicle_id, true)
  populateCustomer(formData.value?.customer_id, formData.value?.customer)
})
</script>

<template>
  <div>
    <dx-load-panel
      :visible="loading"
      :shading="true"
      :show-indicator="true"
      :show-pane="true"
      :hide-on-outside-click="false"
    />
    <dx-form
      ref="formRef"
      :items="formItems"
      :form-data="formData"
      :validation-group="validationGroup"
      :read-only="isReadOnly"
      @content-ready="onFormContentReady"
    >
      <template #generic-caption-template="{ data: cellInfo }">
        <div class="dx-form-group-caption-wrapper">
          <q-icon
            :name="cellInfo.name"
            class="dx-form-group-caption-icon"
          />
          <span class="dx-form-group-caption-text">
            {{ cellInfo.caption }}
          </span>
        </div>
      </template>
      <template #owner-info-caption-template="{ data: cellInfo }">
        <div
          class="dx-form-group-caption"
          style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
        >
          <div class="dx-form-group-caption-wrapper">
            <q-icon
              :name="cellInfo.name"
              class="dx-form-group-caption-icon"
            />
            <span class="dx-form-group-caption-text">
              {{ cellInfo.caption }}
            </span>
          </div>
          <div>
            <q-btn
              icon="mdi-account-switch"
              flat
              size="xs"
              round
              :disable="isReadOnly || isChangeOwnerDisabled"
              @click="addNewVehicleOwner"
            />
            <q-btn
              icon="mdi-content-save"
              flat
              size="xs"
              round
              :disable="isReadOnly || isOwnerSaveDisabled"
              @click="saveNewOwner"
            />
          </div>
        </div>
      </template>
      <template #vehicle-infoBike-search-caption-template="{ data: cellInfo }">
        <div
          class="dx-form-group-caption"
          style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
        >
          <div class="dx-form-group-caption-wrapper">
            <q-icon
              :name="cellInfo.name"
              class="dx-form-group-caption-icon"
            />
            <span class="dx-form-group-caption-text">
              {{ i18n.t('common.infoBikeSearch', {
                credits: garageStore.infoBikePlateSearchCredit?.credits ?? 0
              }) }}
            </span>
          </div>
          <div>
            <info-bike-search-caption-button
              :disabled="isReadOnly || infoBikeSearchDisabled"
              :show-pulse="showPulse"
              :pulse-count="pulseCount"
              :tooltip="i18n.t('common.infoBike')"
              @search="infoBikeSearch"
              @pulse-end="showPulse = false"
            />
          </div>
        </div>
      </template>
      <template #infoBikePlateNumberEditor>
        <info-bike-plate-number-editor
          :cell-info="{ value: formData.infoBike?.plate_number, setValue: v => { formData.infoBike ??= {}; formData.infoBike.plate_number = v } }"
          :readonly="isReadOnly"
          @blur="onPlateBlur"
        />
      </template>
      <template #vehicle-info-caption-template="{ data: cellInfo }">
        <div
          class="dx-form-group-caption"
          style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
        >
          <div class="dx-form-group-caption-wrapper">
            <q-icon
              :name="cellInfo.name"
              class="dx-form-group-caption-icon"
            />
            <span class="dx-form-group-caption-text">
              {{ cellInfo.caption }}
            </span>
          </div>
          <div>
            <q-btn
              icon="mdi-content-save"
              flat
              size="xs"
              round
              :disable="isReadOnly || isVehicleSaveDisabled"
              @click="saveNewVehicle"
            >
              <q-tooltip v-if="isVehicleSaveDisabled && !formData?.vehicle_id">
                {{ i18n.t('common.fill_vehicle_fields_and_owner') }}
              </q-tooltip>
            </q-btn>
          </div>
        </div>
      </template>
      <template #mechanic-info-caption-template="{ data: cellInfo }">
        <div
          class="dx-form-group-caption"
          style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
        >
          <div class="dx-form-group-caption-wrapper">
            <q-icon
              :name="cellInfo.name"
              class="dx-form-group-caption-icon"
            />
            <span class="dx-form-group-caption-text">
              {{ cellInfo.caption }}
            </span>
          </div>
          <div>
            <q-btn
              icon="mdi-plus"
              flat
              size="xs"
              round
              :disable="isReadOnly"
              @click="addNewMechanic"
            />
          </div>
        </div>
      </template>
      <template #mechanicsEditor>
        <dx-tag-box
          v-model="formData.mechanics"
          :items="mechanicOptions"
          :show-selection-controls="true"
          :input-attr="{ 'aria-label': 'Mechanics' }"
          :search-enabled="true"
          apply-value-mode="useButtons"
          :multiline="false"
          :max-displayed-tags="1"
          value-expr="value"
          display-expr="label"
          :disabled="isReadOnly"
          @opened="refreshMechanics"
        >
          <dx-validator :validation-group="validationGroup">
            <dx-custom-rule
              :message="i18n.t('common.required')"
              :validation-callback="mechanicValidation"
            />
          </dx-validator>
        </dx-tag-box>
      </template>
      <template #serviceHistoryRef>
        <div>
          <dx-text-box
            v-if="!formData?.vehicle_id && formData?.vehicle"
            v-model="formData.vehicle.vin"
            :max-length="17"
            :read-only="formRef?.instance?.getEditor('vehicle.vin')?.option('readOnly')"
            :buttons="[dataGridColumn.useButtonsInfoTooltipColumn()]"
            @initialized="onVinInitialized"
            @value-changed="onVinChanged"
          >
            <template #fieldInfoTooltipTemplate>
              <info-tooltip-icon
                :visible="!formData?.vehicle?.plate_number && !formData?.vehicle?.vin"
                :message="i18n.t('common.plateVinHelpInfo')"
              />
            </template>
          </dx-text-box>
          <reference-link
            v-else
            :data="{ prefix: formData?.vehicle?.vin, vin: formData?.vehicle?.vin }"
            :text-fields="['prefix']"
            :query-fields="['vin']"
            :target="menuRoutesEnum.SERVICE_HISTORY"
          />
        </div>
      </template>
      <template #worksheetDocumentTemplate>
        <reference-link
          v-if="formData.worksheets?.length > 0"
          :data="{
            series: formData.worksheets[0]?.series,
            number: formData.worksheets[0]?.number_mnemonic
          }"
          :text-fields="['prefix', 'series', 'number']"
          :query-fields="['series', 'number']"
          :target="menuRoutesEnum.WORKSHEETS"
        >
          <information-tool-tip
            :data="extractReferenceData({ ...formData.worksheets[0], type: portalStore.documentTypesEnum.GARAGE_WORKSHEET })"
            :delay="500"
          />
        </reference-link>
      </template>
      <template #saleDocumentTemplate>
        <reference-link
          v-if="formData.worksheets && formData.worksheets[0]?.sale_document?.id"
          :data="{
            series: formData.worksheets[0]?.sale_document?.series,
            number: formData.worksheets[0]?.sale_document?.number_mnemonic
          }"
          :text-fields="['prefix', 'series', 'number']"
          :query-fields="['series', 'number']"
          :target="menuRoutesEnum.SALE_INVOICES"
        >
          <information-tool-tip
            :data="extractReferenceData(formData.worksheets[0]?.sale_document)"
            :delay="500"
          />
        </reference-link>
      </template>
      <template #appointmentNotifyEditor>
        <dx-tag-box
          v-model="appointmentNotifySelected"
          :items="portalStore.getLocalizedAppointmentNotifyEnums()"
          :show-selection-controls="true"
          :input-attr="{ 'aria-label': 'Notification Methods' }"
          :search-enabled="true"
          apply-value-mode="useButtons"
          :multiline="false"
          :max-displayed-tags="2"
          value-expr="id"
          display-expr="name"
          :disabled="isReadOnly"
        />
      </template>
      <template #customerEditor="{ data: cellInfo }">
        <customer-drop-down-box
          :key="formData.customer_id"
          :data="{data: {customer: registryStore.customers.find(c => c.id === formData.customer_id)} || null}"
          :value="formData.customer_id"
          show-clear-button
          :disabled="isReadOnly"
          @value-changed="onCustomerChanged($event, cellInfo)"
        />
      </template>
      <template #vehicleEditor="{ data: cellInfo }">
        <vehicle-drop-down-box
          :key="formData.vehicle_id"
          :value="formData.vehicle_id"
          show-clear-button
          :disabled="isReadOnly"
          @value-changed="onVehicleChanged($event, cellInfo)"
        />
      </template>
      <template #courtesyVehicleEditor="{ data: cellInfo }">
        <vehicle-drop-down-box
          :key="formData.courtesy_vehicle"
          :value="formData.courtesy_vehicle"
          show-clear-button
          :disabled="isReadOnly"
          @value-changed="onCourtesyVehicleChanged($event, cellInfo)"
        />
      </template>
      <!-- <template #productGridTemplate>  --- US 623 https://dev.azure.com/softway-it/ArcoWeb/_workitems/edit/623
        <garage-event-scheduler-product-grid
          v-model:data="formData.processes"
          :garage-event-id="formData.id"
        />
      </template> -->
      <template #serviceHistoryTemplate>
        <service-history-grid
          v-if="formData.vehicle_id"
          :vehicle="formData.vehicle"
          :options="{ height: '50vh' }"
        />
      </template>
      <template #depositEditor="{ data: cellInfo }">
        <deposit-drop-down-box
          :key="formData.deposit"
          :value="formData.deposit"
          :disabled="isReadOnly"
          @value-changed="onDepositChanged($event, cellInfo)"
        />
      </template>
      <template #interventionTypeCellTemplate>
        <div style="margin-top: 0.5rem">
          <ChipStatus :data="garageStore.getLocalizedInterventionTypes().find(x => x.id === formData.intervention_type_ref)" />
        </div>
      </template>
      <template #carServiceNumberEditor="{ data: cellInfo }">
        <car-service-number-box
          :data="cellInfo"
          :value="formData.car_service"
          :garage-reason-ref="formData.garage_reason_ref"
          :current-car-service="currentCarService"
          :disabled="isReadOnly"
        />
      </template>
      <template #fieldInfoTooltipTemplate>
        <info-tooltip-icon
          :visible="!formData?.vehicle?.plate_number && !formData?.vehicle?.vin"
          :message="i18n.t('common.plateVinHelpInfo')"
        />
      </template>
    </dx-form>
  </div>
</template>

<style scoped lang="scss">
.dx-form-group-caption-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-text {
  line-height: 1.2;
}
:deep(.on-site-waiting-item .dx-field-item-content) {
  padding-top: 0.5rem;
  padding-left: 0;
}
</style>
